import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';

import '@/App.css';
import { getSettings, saveSettings } from '@/lib/storage';
import Header from '@/components/Header';
import SettingsDialog from '@/components/SettingsDialog';
import AmbientSound from '@/components/AmbientSound';
import Landing from '@/pages/Landing';
import Modes from '@/pages/Modes';
import Session from '@/pages/Session';
import History from '@/pages/History';

function Shell() {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettingsState] = useState(getSettings);

  const onChangeSettings = (next) => {
    setSettingsState(next);
    saveSettings(next);
  };

  // Apply midnight theme automatically while in midnight_thoughts session
  useEffect(() => {
    const isMidnight = location.pathname.includes('/session/midnight_thoughts');
    document.documentElement.classList.toggle('midnight', isMidnight);
  }, [location.pathname]);

  const hideHeader = location.pathname.startsWith('/session/');

  return (
    <div className="App relative min-h-screen overflow-x-hidden">
      <Header onOpenSettings={() => setSettingsOpen(true)} hideOnSession={hideHeader} />

      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-10"
        >
          <Routes location={location}>
            <Route
              path="/"
              element={
                <Landing
                  settings={settings}
                  onChangeSettings={onChangeSettings}
                  onOpenSettings={() => setSettingsOpen(true)}
                />
              }
            />
            <Route path="/modes" element={<Modes />} />
            <Route
              path="/session/:mode"
              element={<Session settings={settings} onOpenSettings={() => setSettingsOpen(true)} />}
            />
            <Route path="/history" element={<History />} />
          </Routes>
        </motion.main>
      </AnimatePresence>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onChange={onChangeSettings}
      />

      <AmbientSound enabled={settings.ambientSound} />
      <Toaster position="bottom-center" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
